import React from "react";
import { Icon, notify, confirmDialog } from "../registry";

/* ============================================================
   Google Profile → "Post these photos to your Google profile?"
   (server/src/sites/gbpphotos.js, migration #40). Finished jobs with two or
   more website photos queue here once Google Business Profile is connected:
   Approve (all, or the ones ticked) / Skip, or the owner's own auto-post
   switch (off by default). Until Google approves Fence Works' Business
   Profile API access, an approved job shows "Waiting on Google's approval…"
   and stays in the queue; it goes up on its own afterwards. Nothing is ever
   deleted from the profile.
   ============================================================ */

type QPhoto = { url: string; tag: string; description: string; category: "ADDITIONAL" | "AT_WORK"; chosen: boolean; posted: boolean };
type QItem = {
  id: string; workOrderId: string; label: string; town: string; status: "pending" | "approved" | "posted" | "partial" | "skipped";
  state: null | "waiting_google" | "error"; waitingOnGoogle: boolean; auto: boolean; photos: QPhoto[]; postedCount: number;
  lastError: string | null; nextTryAt: string | null; finishedAt: string | null; createdAt: string | null;
};

/* ---------------- pure helpers (web/scripts/unit-sitegrowth2.mjs) ---------------- */

/* the one line under a job: what happens next */
export function queueStatus(i: Pick<QItem, "status" | "state" | "photos" | "postedCount" | "lastError">) {
  const n = (i.photos || []).length;
  if (i.status === "pending") return { tone: "", text: `${n} photo${n === 1 ? "" : "s"} ready for your Google profile` };
  if (i.state === "waiting_google") return { tone: "amber", text: "Waiting on Google's approval of our Business Profile access" };
  if (i.state === "error") return { tone: "red", text: i.lastError || "Something went wrong — try again." };
  if (i.status === "approved") return { tone: "", text: "Posting…" };
  if (i.status === "posted") return { tone: "green", text: `Posted ${i.postedCount} photo${i.postedCount === 1 ? "" : "s"} to Google` };
  if (i.status === "partial") return { tone: "amber", text: i.lastError || "Some photos didn't go up." };
  return { tone: "", text: "Skipped" };
}

/* the URLs to send: null = all of them (the server's default) */
export function pickedUrls(photos: { url: string }[], picked: Record<string, boolean>) {
  const on = photos.filter((p) => picked[p.url] !== false).map((p) => p.url);
  return on.length === photos.length ? null : on;
}

const fmtShort = (iso: any) => { const t = Date.parse(iso || ""); return t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""; };
const TAG: Record<string, string> = { after: "After", before: "Before", during: "Work in progress" };

export function GbpPhotoQueue({ live }: { live: boolean }) {
  const DB: any = (window as any).VFP_DB;
  const [data, setData] = React.useState<any>(null);
  const [busy, setBusy] = React.useState("");
  const [picked, setPicked] = React.useState<Record<string, Record<string, boolean>>>({});
  const load = React.useCallback(() => {
    if (!DB || !DB.gbpPhotos) return;
    DB.gbpPhotos().then((r: any) => setData(r)).catch((e: any) => setData({ error: e.message || "Couldn't load your job photos." }));
  }, []);
  React.useEffect(() => { if (live) load(); }, [live]);
  if (!live || !data) return null;
  if (data.error) return <div className="card pad gp-card"><b>Job photos for Google</b><p className="muted" style={{ fontSize: 13, margin: "4px 0 0" }}>{data.error}</p></div>;
  if (data.connected === false) return null;

  const items: QItem[] = data.items || [];
  const put = (it: QItem) => setData((x: any) => ({ ...x, items: (x.items || []).map((y: QItem) => (y.id === it.id ? it : y)).filter((y: QItem) => y.status === "pending" || y.status === "approved"), waitingOnGoogle: (x.items || []).map((y: QItem) => (y.id === it.id ? it : y)).some((y: QItem) => y.waitingOnGoogle) }));
  const approve = async (it: QItem) => {
    const urls = pickedUrls(it.photos, picked[it.id] || {});
    if (urls && !urls.length) { notify("Tick at least one photo to post.", { kind: "error" }); return; }
    setBusy(it.id);
    try {
      const r = await DB.gbpPhotoApprove(it.id, urls);
      if (r.waitingOnGoogle) { put(r.item); notify("Approved. " + r.waitingText + " — they'll go up on their own once Google says yes."); }
      else if (r.item.status === "posted" || r.item.status === "partial") {
        notify(r.posted ? `${r.posted} photo${r.posted === 1 ? "" : "s"} posted to your Google profile.` : "Nothing new to post.", { kind: "success" });
        setData((x: any) => ({ ...x, items: (x.items || []).filter((y: QItem) => y.id !== it.id), recent: [...r.item.photos.filter((p: QPhoto) => p.posted).map((p: QPhoto) => ({ url: p.url, category: p.category, postedAt: new Date().toISOString() })), ...(x.recent || [])].slice(0, 12) }));
      } else { put(r.item); if (r.item.lastError) notify(r.item.lastError, { kind: "error" }); }
    } catch (e: any) { notify(e.message || "Couldn't post those — try again.", { kind: "error" }); }
    setBusy("");
  };
  const skip = async (it: QItem) => {
    if (!(await confirmDialog({ title: "Skip these photos?", body: "They won't be offered for your Google profile again. They stay on the job and your website either way.", confirmLabel: "Skip" }))) return;
    setBusy(it.id);
    try { await DB.gbpPhotoSkip(it.id); setData((x: any) => ({ ...x, items: (x.items || []).filter((y: QItem) => y.id !== it.id) })); }
    catch (e: any) { notify(e.message || "Couldn't skip it — try again.", { kind: "error" }); }
    setBusy("");
  };
  const setAuto = async (on: boolean) => {
    const prev = data.auto;
    setData({ ...data, auto: on });
    try { await DB.gbpPhotoAuto(on); notify(on ? "New job photos will go to your Google profile on their own." : "Auto-post off. New job photos wait here for you."); if (on) load(); }
    catch (e: any) { setData((x: any) => ({ ...x, auto: prev })); notify(e.message || "Couldn't save that", { kind: "error" }); }
  };
  const toggle = (it: QItem, url: string) => setPicked((x) => ({ ...x, [it.id]: { ...(x[it.id] || {}), [url]: (x[it.id] || {})[url] === false } }));

  return (
    <div className="card gp-card" data-gbp-photos>
      <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <span className="kicker kicker-teal">Job photos → Google</span>
          <h2 className="display" style={{ fontSize: 20, margin: "6px 0 2px" }}>Post these photos to your Google profile?</h2>
          <p className="muted" style={{ fontSize: 13, margin: 0, maxWidth: 640 }}>Fresh photos of your own work help your profile show up in the map pack. Finished jobs with two or more photos for your website land here.</p>
        </div>
        <label className="gp-auto">
          <button type="button" className={"sw" + (data.auto ? " on" : "")} role="switch" aria-checked={!!data.auto} aria-label="Post new job photos automatically" disabled={data.setup === false} onClick={() => setAuto(!data.auto)}><span className="knob" /></button>
          <span><b>Post new job photos automatically</b><span className="faint">Off: each job waits for your OK.</span></span>
        </label>
      </div>

      {data.setup === false && <p className="ws-note" style={{ marginTop: 10 }}><Icon name="lock" size={12} /> {data.message}</p>}
      {data.waitingOnGoogle && (
        <div className="gp-wait" role="status"><Icon name="clock" size={14} /><span><b>{data.waitingText || "Waiting on Google's approval of our Business Profile access"}.</b> Your approved photos stay in the queue and go up on their own once Google says yes. Nothing for you to do.</span></div>
      )}

      {data.setup !== false && !items.length && <p className="muted gp-empty">Nothing waiting. When you finish a job with two or more photos that can go on your website, they show up here.</p>}
      {items.map((it) => {
        const st = queueStatus(it);
        const sel = picked[it.id] || {};
        const nSel = it.photos.filter((p) => sel[p.url] !== false).length;
        const pending = it.status === "pending";
        return (
          <div key={it.id} className={"gp-item" + (it.waitingOnGoogle ? " waiting" : "")} data-status={it.waitingOnGoogle ? "waiting" : it.status}>
            <div className="gp-item-head">
              <div style={{ minWidth: 0 }}>
                <b>{it.label}</b>
                <span className="faint">{[it.town, it.finishedAt ? "finished " + fmtShort(it.finishedAt) : ""].filter(Boolean).join(" · ")}</span>
              </div>
              <span className={"chip" + (st.tone ? " " + st.tone : "")}>{it.waitingOnGoogle ? "Waiting on Google" : pending ? `${it.photos.length} photos` : st.tone === "red" ? "Didn't post" : "Approved"}</span>
            </div>
            <div className="gp-photos">
              {it.photos.map((p) => {
                const on = pending ? sel[p.url] !== false : p.chosen;
                return (
                  <button key={p.url} type="button" className={"gp-photo" + (on ? " on" : "")} disabled={!pending} aria-pressed={on} title={p.description} onClick={() => toggle(it, p.url)}>
                    <img src={p.url} alt={p.description} loading="lazy" />
                    {pending && <span className="gp-check"><Icon name={on ? "check" : "plus"} size={12} /></span>}
                    {TAG[p.tag] && <span className="gp-tag">{TAG[p.tag]}</span>}
                    {p.posted && <span className="gp-tag gp-posted">On Google</span>}
                  </button>
                );
              })}
            </div>
            {!pending && <p className={"gp-status " + st.tone}>{st.text}{it.waitingOnGoogle && it.nextTryAt ? <span className="faint"> · next try {fmtShort(it.nextTryAt)}</span> : null}</p>}
            <div className="ws-sugg-actions">
              {pending ? (
                <>
                  <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || !nSel} onClick={() => approve(it)}><Icon name="check" size={13} />{busy === it.id ? "Posting…" : nSel === it.photos.length ? `Approve · post ${nSel}` : `Post ${nSel} of ${it.photos.length}`}</button>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => skip(it)}>Skip</button>
                  <span className="faint gp-hint">Tap a photo to leave it out.</span>
                </>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => approve(it)}><Icon name="undo" size={13} />{busy === it.id ? "Trying…" : "Try again now"}</button>
              )}
            </div>
          </div>
        );
      })}

      {(data.recent || []).length > 0 && (
        <div className="gp-recent">
          <div className="wg-sub">Posted to Google</div>
          <div className="gp-recent-strip">
            {(data.recent || []).slice(0, 8).map((r: any) => <img key={r.url} src={r.url} alt="" title={"Posted " + fmtShort(r.postedAt)} loading="lazy" />)}
          </div>
          <p className="faint" style={{ fontSize: 12, margin: "6px 0 0" }}>They stay on your profile. Fence Works never deletes anything from it — remove a photo in Google if you ever need to.</p>
        </div>
      )}
    </div>
  );
}
